import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";

function authHeaders() {
  const token = sessionStorage.getItem("syrakis_token");
  return { Accept: "application/json", "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function copy(value) {
  return JSON.parse(JSON.stringify(value));
}

function updateMap(setConfig, mapName, key, field, value) {
  setConfig((current) => ({
    ...current,
    [mapName]: {
      ...(current[mapName] || {}),
      [key]: { ...(current[mapName]?.[key] || {}), [field]: field === "order" ? Number(value || 0) : field === "visible" ? Boolean(value) : value },
    },
  }));
}

export default function MenuManagementPage() {
  const [features, setFeatures] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [config, setConfig] = useState({});
  const [status, setStatus] = useState("");
  const selected = useMemo(() => features.find((feature) => feature.id === selectedId) || null, [features, selectedId]);

  async function load() {
    const response = await fetch("/api/menu/features", { credentials: "same-origin", headers: authHeaders() });
    if (!response.ok) throw new Error("No se pudieron cargar las plantillas.");
    const data = await response.json();
    setFeatures(data.features || []);
    if (!selectedId && data.features?.[0]) setSelectedId(data.features[0].id);
    const first = data.features?.find((feature) => feature.id === selectedId) || data.features?.[0];
    if (first) setConfig(copy(first.config || {}));
  }

  useEffect(() => { load().catch((error) => setStatus(error.message)); }, []);

  useEffect(() => {
    const feature = features.find((item) => item.id === selectedId);
    if (feature) setConfig(copy(feature.config || {}));
  }, [features, selectedId]);

  async function save() {
    setStatus("Guardando...");
    const response = await fetch(`/api/menu/features/${encodeURIComponent(selectedId)}`, { method: "PUT", credentials: "same-origin", headers: authHeaders(), body: JSON.stringify({ config }) });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) { setStatus(data.error || "No se pudo guardar."); return; }
    setStatus("Configuración guardada.");
    await load();
  }

  async function restore() {
    if (!window.confirm("¿Restaurar la plantilla original de esta feature?")) return;
    const response = await fetch(`/api/menu/features/${encodeURIComponent(selectedId)}/restore`, { method: "POST", credentials: "same-origin", headers: authHeaders() });
    if (!response.ok) { setStatus("No se pudo restaurar la plantilla."); return; }
    setStatus("Plantilla original restaurada.");
    await load();
  }

  function addSection() {
    const label = window.prompt("Nombre de la nueva sección");
    if (!label) return;
    const key = `custom:section:${Date.now()}`;
    setConfig((current) => ({ ...current, custom_sections: [...(current.custom_sections || []), { key, label, icon: "solar:widget-linear", order: 1000, visible: true }] }));
  }

  function addGroup(sectionKey) {
    const label = window.prompt("Nombre del nuevo subgrupo");
    if (!label) return;
    const key = `custom:group:${Date.now()}`;
    setConfig((current) => ({ ...current, custom_groups: [...(current.custom_groups || []), { key, section_key: sectionKey, label, icon: "solar:list-linear", order: 1000, visible: true }] }));
  }

  function addUrl(sectionKey, groupKey) {
    const label = window.prompt("Nombre visible del enlace");
    const url = label && window.prompt("URL (https://... o ruta interna no API)");
    if (!label || !url) return;
    setConfig((current) => ({ ...current, custom_items: [...(current.custom_items || []), { key: `custom:item:${Date.now()}`, section_key: sectionKey, group_key: groupKey, label, url, icon: "solar:link-linear", order: 1000, visible: true }] }));
  }

  return (
    <div className="dashboard-main-body">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-24">
        <div><h4 className="mb-8">Menú transversal</h4><p className="text-secondary-light mb-0">Edita presentación y orden. Las rutas técnicas y permisos se mantienen registrados por la plataforma.</p></div>
        <div className="d-flex gap-2"><button className="btn btn-outline-primary" type="button" onClick={addSection}><Icon icon="solar:add-circle-linear" /> Sección</button><button className="btn btn-primary" type="button" onClick={save} disabled={!selected}>Guardar cambios</button><button className="btn btn-outline-danger" type="button" onClick={restore} disabled={!selected}>Restaurar original</button></div>
      </div>
      {status && <div className="alert alert-info">{status}</div>}
      <div className="card p-24 mb-20">
        <label className="form-label">Feature</label>
        <select className="form-select" value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>
          {features.map((feature) => <option key={feature.id} value={feature.id}>{feature.nombre || feature.id}</option>)}
        </select>
      </div>
      {selected && <div className="row gy-20">
        {(selected.template.sections || []).map((section) => {
          const sectionOverride = config.section_overrides?.[section.key] || {};
          return <div className="col-12" key={section.key}>
            <div className="card p-20">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-16">
                <input className="form-control" style={{ maxWidth: 280 }} value={sectionOverride.label ?? section.label} onChange={(event) => updateMap(setConfig, "section_overrides", section.key, "label", event.target.value)} />
                <input className="form-control" style={{ maxWidth: 220 }} value={sectionOverride.icon ?? section.icon} onChange={(event) => updateMap(setConfig, "section_overrides", section.key, "icon", event.target.value)} />
                <input className="form-control" type="number" style={{ maxWidth: 110 }} value={sectionOverride.order ?? section.order} onChange={(event) => updateMap(setConfig, "section_overrides", section.key, "order", event.target.value)} />
                <label className="form-check ms-2"><input className="form-check-input" type="checkbox" checked={sectionOverride.visible ?? section.visible !== false} onChange={(event) => updateMap(setConfig, "section_overrides", section.key, "visible", event.target.checked)} /> visible</label>
                <button className="btn btn-sm btn-outline-primary ms-auto" type="button" onClick={() => addGroup(section.key)}>+ Subgrupo</button>
              </div>
              {(section.groups || []).map((group) => {
                const groupOverride = config.group_overrides?.[group.key] || {};
                return <div className="border rounded p-16 mb-12" key={group.key}>
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-12">
                    <input className="form-control" style={{ maxWidth: 240 }} value={groupOverride.label ?? group.label} onChange={(event) => updateMap(setConfig, "group_overrides", group.key, "label", event.target.value)} />
                    <input className="form-control" style={{ maxWidth: 200 }} value={groupOverride.icon ?? group.icon} onChange={(event) => updateMap(setConfig, "group_overrides", group.key, "icon", event.target.value)} />
                    <input className="form-control" type="number" style={{ maxWidth: 100 }} value={groupOverride.order ?? group.order} onChange={(event) => updateMap(setConfig, "group_overrides", group.key, "order", event.target.value)} />
                    <button className="btn btn-sm btn-outline-secondary ms-auto" type="button" onClick={() => addUrl(section.key, group.key)}>+ URL personalizada</button>
                  </div>
                  {(group.items || []).map((item) => {
                    const override = config.item_overrides?.[item.key] || {};
                    return <div className="row align-items-center gy-2 mb-8" key={item.key}>
                      <div className="col-lg-4"><input className="form-control" value={override.label ?? item.label} onChange={(event) => updateMap(setConfig, "item_overrides", item.key, "label", event.target.value)} /></div>
                      <div className="col-lg-3"><input className="form-control" value={override.icon ?? item.icon} onChange={(event) => updateMap(setConfig, "item_overrides", item.key, "icon", event.target.value)} /></div>
                      <div className="col-lg-2"><input className="form-control" type="number" value={override.order ?? item.order} onChange={(event) => updateMap(setConfig, "item_overrides", item.key, "order", event.target.value)} /></div>
                      <div className="col-lg-2"><label className="form-check"><input className="form-check-input" type="checkbox" checked={override.visible ?? item.visible !== false} onChange={(event) => updateMap(setConfig, "item_overrides", item.key, "visible", event.target.checked)} /> visible</label></div>
                      <div className="col-lg-1 text-secondary-light" title="Ruta técnica protegida"><Icon icon="solar:lock-keyhole-linear" /></div>
                    </div>;
                  })}
                </div>;
              })}
            </div>
          </div>;
        })}
      </div>}
    </div>
  );
}
